import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ON_LOCATION_DELIVERY_TAG, NETFLIX_ROWS, PORTFOLIO_ITEMS } from '../data/portfolioData';
import { PORTFOLIO_DE } from '../data/portfolioDe';
import { useLang, localizeItem } from '../i18n/LanguageContext';
import styles from './Work.module.css';

function PlayIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="19" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
      <path d="M17 14l12 6-12 6V14z" fill="rgba(255,255,255,0.7)" />
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12 5l-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Mobile breakpoint must match the CSS breakpoint that switches card aspect ratio
const MOBILE_QUERY = '(max-width: 679px)';

function CardThumb({ item }) {
  const { t, buildPath, lang } = useLang();
  const loc = localizeItem(item, lang, PORTFOLIO_DE);
  const onLocationLabel = t('row.On-location Delivery');
  const badgeLabel = loc.badge ? t(`badge.${loc.badge}`) : loc.badge;

  const content = (
    <>
      {loc.posterSrc && (
        <picture>
          {loc.posterSrcVertical && (
            <source
              media={MOBILE_QUERY}
              srcSet={`${loc.posterSrcVertical.replace('.webp', '-sm.webp')} 1x, ${loc.posterSrcVertical} 2x`}
            />
          )}
      <img
        src={loc.posterSrc}
        alt={`${loc.title} project thumbnail`}
        className={styles.poster}
        loading="lazy"
        decoding="async"
      />
        </picture>
      )}
      <PlayIcon />
      {loc.categories.includes('On-location Delivery') && (
        <span className={styles.onLocationBadge}>{onLocationLabel}</span>
      )}
      {loc.badge && (
        <span className={styles.seriesBadge}>{badgeLabel}</span>
      )}
    </>
  );

  if (loc.slug && loc.videoSrc) {
    const targetSlug = loc.campaignSlug || loc.slug;
    return (
      <Link
        to={buildPath(`/work/${targetSlug}`)}
        className={styles.thumbnail}
        aria-label={`Open ${loc.title} case study`}
      >
        {content}
      </Link>
    );
  }
  return <div className={styles.thumbnail}>{content}</div>;
}

// ── Single row with arrow navigation ────────────────────────────────────────

function slugifyRow(label) {
  return `work-${label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;
}

function Row({ label, items }) {
  const { t, lang } = useLang();
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.75;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  const displayLabel = t(`row.${label}`);
  const locItems = items.map((it) => localizeItem(it, lang, PORTFOLIO_DE));

  return (
    <div id={slugifyRow(label)} className={styles.row}>
      <h2 className={styles.rowLabel}>{displayLabel}</h2>
      <div className={styles.rowOuter}>
        {canLeft && (
          <button
            className={`${styles.scrollBtn} ${styles.scrollBtnLeft}`}
            onClick={() => scroll(-1)}
            aria-label={`Scroll ${displayLabel} left`}
          >
            <ChevronLeft />
          </button>
        )}

        <div className={styles.rowTrack} ref={trackRef}>
          {locItems.map((item) => (
            <article key={item.id} className={styles.card}>
              <div className={styles.thumbWrap}>
                <CardThumb item={item} />
              </div>
              <h3 className={styles.cardTitle}>{item.title}</h3>
            </article>
          ))}
        </div>

        {canRight && (
          <button
            className={`${styles.scrollBtn} ${styles.scrollBtnRight}`}
            onClick={() => scroll(1)}
            aria-label={`Scroll ${displayLabel} right`}
          >
            <ChevronRight />
          </button>
        )}
      </div>
    </div>
  );
}

// Brand & Live row order (by portfolio id)
const BRAND_LIVE_ORDER = [25, 21, 41, 29, 19, 31];

// Small Events row order (by portfolio id)
const SMALL_EVENTS_ORDER = [20, 26, 35, 33, 31];

// Row definitions
const ROWS = NETFLIX_ROWS.map((label) => {
  if (label === 'Selected Work') {
    return {
      label,
      items: PORTFOLIO_ITEMS.filter((i) => i.inMix).sort(
        (a, b) => (a.mixOrder ?? 99) - (b.mixOrder ?? 99),
      ),
    };
  }
  if (label === 'Brand & Live') {
    return {
      label,
      items: PORTFOLIO_ITEMS.filter((i) => i.categories.includes(label)).sort(
        (a, b) => {
          const ai = BRAND_LIVE_ORDER.indexOf(a.id);
          const bi = BRAND_LIVE_ORDER.indexOf(b.id);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        },
      ),
    };
  }
  if (label === 'Small Events') {
    return {
      label,
      items: PORTFOLIO_ITEMS.filter((i) => i.categories.includes(label)).sort(
        (a, b) => {
          const ai = SMALL_EVENTS_ORDER.indexOf(a.id);
          const bi = SMALL_EVENTS_ORDER.indexOf(b.id);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        },
      ),
    };
  }
  return {
    label,
    items: PORTFOLIO_ITEMS.filter((i) => i.categories.includes(label)),
  };
}).filter((row) => row.items.length > 0);

// ── Root component ───────────────────────────────────────────────────────────

export default function Work() {
  return (
    <section id="work" className={styles.section}>
      {ROWS.map((row) => (
        <Row key={row.label} label={row.label} items={row.items} />
      ))}
    </section>
  );
}
